import { createSlice } from "@reduxjs/toolkit"
import client from "../../lib/client"
import { createFilterUrl } from "../../functions/objectFunctions/createFilterUrl"

import {
    clientReportsUrl,
    clientReportUrl,
    generatedReportsUrl,
    generatedReportAssignablesUrl,
    generatedReportsTableUrl,
    generatedReportUrl,
    deleteGeneratedReportUrl,
    bulkDeleteGeneratedReportsUrl,
} from "../../lib/endpoints"
import { notification } from "antd"

export const reportsSlice = createSlice({
    name: "reports",
    initialState: {
        loading: false,
        reports: [],
        report: null,
        assignables: [],
        pagination: { current: 1, pageSize: 10 },
        generatedReports: [],
        generatedReportsTotal: 0,
        generatedReport: null,
    },
    reducers: {
        setLoading: (state, action) => {
            state.loading = action.payload
        },
        setReports: (state, action) => {
            state.reports = action.payload
        },
        setReport: (state, action) => {
            state.report = action.payload
        },
        setPagination: (state, action) => {
            state.pagination = action.payload
        },
        setGeneratedReports: (state, action) => {
            state.generatedReports = action.payload.generated_reports
            state.generatedReportsTotal = action.payload.total
        },
        setGeneratedReport: (state, action) => {
            state.generatedReport = action.payload
        },
        updateGeneratedReportFile: (state, action) => {
            try {
                state.generatedReports = state.generatedReports.map((gr) => {
                    if (gr.id === action.payload.generated_report_id) {
                        return {
                            ...gr,
                            file_url: action.payload.generated_report_file,
                            extra_file_url: action.payload.generated_report_extra_file,
                            // `?? null` keeps the row shape consistent against a GenerateReportJob
                            // that doesn't broadcast sizes yet, rather than writing `undefined`.
                            file_size: action.payload.generated_report_file_size ?? null,
                            extra_file_size: action.payload.generated_report_extra_file_size ?? null,
                        }
                    } else {
                        return gr
                    }
                })
            } catch (e) {
                console.error("Report generation error: ", e)
                return state
            }
        },
    },
})

export const { setReports, setLoading, setReport, setPagination, setGeneratedReports, setGeneratedReport, updateGeneratedReportFile } = reportsSlice.actions

export const selectReports = (state) => state.reports.reports
export const selectLoading = (state) => state.reports.loading
export const selectReport = (state) => state.reports.report
export const selectPagination = (state) => state.reports.pagination
export const selectGeneratedReports = (state) => state.reports.generatedReports
export const selectGeneratedReportsTotal = (state) => state.reports.generatedReportsTotal
export const selectGeneratedReport = (state) => state.reports.generatedReport

export function fetchReports(clientId) {
    return async (dispatch, getState) => {
        dispatch(setLoading(true))

        client
            .get(clientReportsUrl(clientId))
            .then(({ data }) => {
                // console.log(data)
                dispatch(setReports(data))
            })
            .catch((e) => {
                console.warn(e)
            })
            .finally(() => {
                dispatch(setLoading(false))
            })
    }
}

export function fetchReport(clientId, reportId) {
    return async (dispatch, getState) => {
        dispatch(setLoading(true))

        client
            .get(clientReportUrl(clientId, reportId))
            .then(({ data }) => {
                dispatch(setReport(data))
            })
            .catch((e) => {
                console.warn(e)
            })
            .finally(() => {
                dispatch(setLoading(false))
            })
    }
}

export function fetchGeneratedReport(clientId, reportId, generatedReportId) {
    return async (dispatch, getState) => {
        dispatch(setLoading(true))

        client
            .get(generatedReportUrl(clientId, reportId, generatedReportId))
            .then(({ data }) => {
                dispatch(setGeneratedReport(data))
            })
            .catch((e) => {
                console.warn(e)
            })
            .finally(() => {
                dispatch(setLoading(false))
            })
    }
}

export function generateReport(clientId, reportId, values) {
    return async (dispatch, getState) => {
        dispatch(setLoading(true))

        const response = await client.post(`${generatedReportsUrl(clientId, reportId)}?connection_id=${getState().app.uid}`, {
            generated_report: values,
        })

        dispatch(setLoading(true))

        return response
    }
}

export function updateGeneratedReport(clientId, reportId, generatedReportId, values) {
    return async (dispatch, getState) => {
        dispatch(setLoading(true))

        const response = await client.patch(`${generatedReportUrl(clientId, reportId, generatedReportId)}?connection_id=${getState().app.uid}`, {
            generated_report: values,
        })

        dispatch(setLoading(true))

        return response
    }
}

export function fetchAssignables(clientId, reportId, type) {
    return async (dispatch, getState) => {
        const response = await client.get(generatedReportAssignablesUrl(clientId, reportId, type))

        return response
    }
}

/// The delete endpoints answer 200 for every outcome, so success/failure lives in the body.
/// `message` is a plain string on the delete paths but an array of strings on the
/// validation/authorization paths -- normalize before handing it to antd, which would
/// otherwise render an array run together with no separators.
const normalizeMessage = (message) => (Array.isArray(message) ? message.join(" ") : message)

export function deleteReport(id, query){
    return async (dispatch, getState) => {
        dispatch(setLoading(true))
        client
            .delete(deleteGeneratedReportUrl(id))
            .then(({ data }) => {
                /// `=== false` rather than `!data.success` so a response that omits the field
                /// keeps behaving the way it always has
                if (data?.success === false) {
                    notification.error({
                        duration: 4,
                        message: "Unable to delete report",
                        description: normalizeMessage(data?.message),
                    })
                    dispatch(setLoading(false))

                    return
                }

                notification.success({
                    duration: 2,
                    message: "Report Deleted",
                    description: "The selected report has been deleted."

                })
                dispatch(filterGeneratedReports(query))
            })
            .catch((e) => {
                console.log(e);
                notification.error({
                    duration: 1.5,
                    message: "There was an error.",
                })
                dispatch(setLoading(false))
            })
    }
}

export function bulkDeleteReports(ids, query) {
    return async (dispatch) => {
        dispatch(setLoading(true))

        try {
            const { data } = await client.post(bulkDeleteGeneratedReportsUrl(), { ids: ids })

            const deleted = data?.deleted_ids?.length ?? 0
            const failed = data?.failed_ids?.length ?? 0
            const missing = data?.missing_ids?.length ?? 0
            const description = normalizeMessage(data?.message)

            if (!deleted && !failed && !missing) {
                /// nothing was touched -- empty selection, over the cap, or not on the allowlist
                notification.error({
                    duration: 4,
                    message: "Unable to delete reports",
                    description: description,
                })
            } else if (failed || missing) {
                /// `success` is still true when every id was already gone, so branch on the
                /// counts instead of on `success` and warn rather than claim a clean delete
                notification.warning({
                    duration: 4,
                    message: deleted ? "Some reports were not deleted" : "No reports were deleted",
                    description: description,
                })
            } else {
                notification.success({
                    duration: 2,
                    message: "Reports Deleted",
                    description: description,
                })
            }

            if (deleted || missing) {
                /// the list is stale either way -- rows someone else already deleted still need
                /// clearing. filterGeneratedReports owns `loading` from here.
                dispatch(filterGeneratedReports(query))
            } else {
                dispatch(setLoading(false))
            }

            return data
        } catch (e) {
            console.warn(e)
            notification.error({
                duration: 4,
                message: "There was an error.",
                description: "The reports could not be deleted.",
            })
            dispatch(setLoading(false))

            return null
        }
    }
}

export function fetchGeneratedReports(clientId, pagination) {
    return async (dispatch, getState) => {
        dispatch(setLoading(true))

        client
            .get(`${generatedReportsTableUrl(clientId)}?page=${pagination.current}&per=${pagination.pageSize}`)
            .then(({ data }) => {
                dispatch(setPagination(pagination))
                dispatch(setGeneratedReports(data))
            })
            .catch((e) => {
                console.warn(e)
            })
            .finally(() => {
                dispatch(setLoading(false))
            })
    }
}

export function filterGeneratedReports(query) {
    /// create the filter URL string
    let filterUrl = createFilterUrl(query, generatedReportsTableUrl(query.clientId))

    return async (dispatch) => {
        dispatch(setLoading(true))
        client
            .get(filterUrl)
            .then((response) => {
                dispatch(setPagination(query.pagination))
                dispatch(setGeneratedReports(response.data))
            })
            .catch((error) => {
                console.warn(error)
            })
            .finally(() => {
                dispatch(setLoading(false))
            })
    }
}

export default reportsSlice.reducer
